// tabs/KPIsTab.jsx
import React, { useMemo, useState, useEffect } from "react";

export default function KPIsTab({
  goalTotal = 440,
  phase1Goal = 150,
  regionGoals = { SEA: 220, India: 220 },
}) {
  const [actualsData, setActualsData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Landing Page Map for KPI 2
  const assetLinks = {
    "APAC Global Skills Report":
      "https://networkinginfonet.com/Coursera/get-the-2025-global-skills/F04F265F-F0D1-48A1-A8CB-AB586A617D1",
    "Unlocking Productivity":
      "https://networkinginfonet.com/Coursera/unlocking-productivity/1E56C26B-2E2A-49CC-B6B2-0BF891FBB5B2",
    "Job Skills Report":
      "https://networkinginfonet.com/Coursera/job-skills-report-sec/0EF27D32-6149-4746-A0C8-0B225493E594",
  };

  useEffect(() => {
    fetch("/APAC_Reporting_India_SEA.csv")
      .then((res) => res.text())
      .then((text) => {
        const rows = [];
        let cells = [];
        let current = "";
        let inQuotes = false;

        for (let i = 0; i < text.length; i++) {
          const char = text[i];
          const next = text[i + 1];
          if (char === '"') {
            if (inQuotes && next === '"') {
              current += '"';
              i++;
            } else inQuotes = !inQuotes;
          } else if (!inQuotes && char === ",") {
            cells.push(current.trim());
            current = "";
          } else if (
            !inQuotes &&
            (char === "\n" || (char === "\r" && next === "\n"))
          ) {
            if (char === "\r") i++;
            cells.push(current.trim());
            rows.push(cells);
            cells = [];
            current = "";
          } else {
            current += char;
          }
        }
        if (current || cells.length > 0) {
          cells.push(current.trim());
          rows.push(cells);
        }

        const validLeads = rows
          .slice(1)
          .map((row) => ({
            title: row[0],
            country: row[1],
            industry: row[3],
            priority: row[4],
            company: row[5],
            asset: row[7],
            date: row[9],
          }))
          .filter((lead) => lead.date && lead.date.includes("/"));

        setActualsData(validLeads);
        setLoading(false);
      });
  }, []);

  const stats = useMemo(() => {
    const kpi = {
      asOf: "N/A",
      total: actualsData.length,
      byRegion: { SEA: 0, India: 0 },
      byAsset: {},
      byTitleBucket: {
        "L&D": { planned: 176, actual: 0 },
        HR: { planned: 176, actual: 0 },
        Tech: { planned: 88, actual: 0 },
      },
      questionBreakdown: {
        V1: {
          total: 0,
          answers: {
            "Business growth potential": 0,
            "Ability to innovate quickly": 0,
            "Cost efficiency": 0,
            "Market competitiveness": 0,
            "Compliance requirements": 0,
            "No budget": 0,
          },
        },
        V2: {
          total: 0,
          answers: {
            "Cost per learner": 0,
            "Projected ROI": 0,
            "Alignment with KPIs": 0,
            "Compliance needs": 0,
            "Training scalability": 0,
            "No budget": 0,
          },
        },
      },
    };

    const v1Keys = Object.keys(kpi.questionBreakdown.V1.answers);

    actualsData.forEach((lead) => {
      // 1. Logic for "As Of" Date
      if (kpi.asOf === "N/A" || new Date(lead.date) > new Date(kpi.asOf))
        kpi.asOf = lead.date;

      // 2. Region Logic
      if (lead.country?.toLowerCase() === "india") kpi.byRegion.India++;
      else kpi.byRegion.SEA++;

      // 3. ASSET BUBBLE UP LOGIC
      let aName = lead.asset || "Other Assets";
      if (aName.toUpperCase().includes("GLOBAL SKILLS REPORT")) {
        aName = "APAC Global Skills Report";
      } else if (aName.toUpperCase().includes("PRODUCTIVITY")) {
        aName = "Unlocking Productivity";
      } else if (aName.toUpperCase().includes("JOB SKILLS")) {
        aName = "Job Skills Report";
      }
      kpi.byAsset[aName] = (kpi.byAsset[aName] || 0) + 1;

      // 4. Function/Title Mapping
      const t = lead.title?.toUpperCase() || "";
      if (t.includes("HR") || t.includes("PEOPLE"))
        kpi.byTitleBucket.HR.actual++;
      else if (t.includes("L&D") || t.includes("LEARN") || t.includes("TALENT"))
        kpi.byTitleBucket["L&D"].actual++;
      else if (t.includes("TECH") || t.includes("IT") || t.includes("DIGITAL"))
        kpi.byTitleBucket.Tech.actual++;

      // 5. Survey Logic (Normalize case for comparison)
      const ans = lead.priority;
      if (v1Keys.some((k) => k.toLowerCase() === ans?.toLowerCase())) {
        const key = v1Keys.find((k) => k.toLowerCase() === ans?.toLowerCase());
        kpi.questionBreakdown.V1.answers[key]++;
        kpi.questionBreakdown.V1.total++;
      } else if (
        Object.keys(kpi.questionBreakdown.V2.answers).some(
          (k) => k.toLowerCase() === ans?.toLowerCase()
        )
      ) {
        const key = Object.keys(kpi.questionBreakdown.V2.answers).find(
          (k) => k.toLowerCase() === ans?.toLowerCase()
        );
        kpi.questionBreakdown.V2.answers[key]++;
        kpi.questionBreakdown.V2.total++;
      }
    });

    return kpi;
  }, [actualsData]);

  const pct = (num, den) => (den ? Math.round((num / den) * 100) : 0);

  if (loading)
    return (
      <div style={{ padding: 40, color: "#64748B" }}>
        Synchronizing Regional Metrics...
      </div>
    );

  return (
    <section className="section">
      <div className="chips" style={{ marginBottom: 10 }}>
        <span className="pill">KPIs</span>
        <span className="pill green">Live 201 Leads</span>
        <span className="pill">As of {stats.asOf}</span>
      </div>

      {/* KPI 1: Lead Volume */}
      <div className="card lg">
        <div className="section-head">
          <h3 className="h2">KPI 1 · Campaign Volume</h3>
          <span className="muted xs">
            Target {goalTotal} · Current Actual {stats.total}
          </span>
        </div>
        <div className="grid md-g-3">
          <div className="kpi">
            <div className="label">Actual Leads</div>
            <div className="val" style={{ color: "#10b981" }}>
              {stats.total}
            </div>
          </div>
          <div className="kpi">
            <div className="label">Total Goal</div>
            <div className="val">{goalTotal}</div>
          </div>
          <div className="kpi">
            <div className="label">% of Total Goal</div>
            <div className="val">{pct(stats.total, goalTotal)}%</div>
          </div>
        </div>
      </div>

      {/* KPI 2: Asset Normalization (Bubbled Up) */}
      <div className="card lg section">
        <h3 className="h2">KPI 2 · Asset Engagement (Bubbled Up)</h3>
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Strategic Asset Category</th>
                <th>LP</th>
                <th style={{ textAlign: "right" }}>Leads Delivered</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(stats.byAsset)
                .sort((a, b) => b[1] - a[1])
                .map(([name, count]) => (
                  <tr key={name}>
                    <td className="sm">
                      <strong>{name}</strong>
                    </td>
                    <td className="sm">
                      {assetLinks[name] ? (
                        <a
                          href={assetLinks[name]}
                          target="_blank"
                          rel="noreferrer"
                          className="btn secondary link-btn"
                        >
                          View LP ↗
                        </a>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td
                      className="sm"
                      style={{ textAlign: "right", fontWeight: 700 }}
                    >
                      {count}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* KPI 3: Title Strategy */}
      <div className="card lg section">
        <h3 className="h2">KPI 3 · Title Strategy Performance</h3>
        <table className="tbl">
          <thead>
            <tr>
              <th>Functional Bucket</th>
              <th>Target Mix</th>
              <th>Actual Count</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(stats.byTitleBucket).map(([fn, v]) => (
              <tr key={fn}>
                <td className="sm">{fn}</td>
                <td className="sm">{v.planned}</td>
                <td className="sm">{v.actual}</td>
                <td
                  className="sm"
                  style={{
                    color: v.actual >= v.planned ? "#10b981" : "inherit",
                  }}
                >
                  {pct(v.actual, v.planned)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* KPI 4: Priority Mapping */}
      <div className="grid g-2 section">
        <div className="card">
          <h4 className="h2">V1: Investment Priorities</h4>
          <table className="tbl">
            <tbody>
              {Object.entries(stats.questionBreakdown.V1.answers).map(
                ([a, n]) => (
                  <tr key={a}>
                    <td className="sm">{a}</td>
                    <td className="sm" style={{ textAlign: "right" }}>
                      {n}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
        <div className="card">
          <h4 className="h2">V2: Evaluation Factors</h4>
          <table className="tbl">
            <tbody>
              {Object.entries(stats.questionBreakdown.V2.answers).map(
                ([a, n]) => (
                  <tr key={a}>
                    <td className="sm">{a}</td>
                    <td className="sm" style={{ textAlign: "right" }}>
                      {n}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
